import { QueryClient } from "@tanstack/react-query";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";

// Cache configuration constants
export const CACHE_CONFIG = {
  // Stale times for different data types (how long data stays fresh)
  STATIC_DATA: 30 * 60 * 1000,      // 30 minutes (categories, static content)
  USER_DATA: 5 * 60 * 1000,         // 5 minutes (profile, orders)
  PRODUCT_DATA: 15 * 60 * 1000,     // 15 minutes (products, shops)
  SEARCH_DATA: 10 * 60 * 1000,      // 10 minutes (search results)
  ANALYTICS_DATA: 2 * 60 * 1000,    // 2 minutes (dashboard analytics)
  // Cache times (how long to keep data in cache)
  DEFAULT_CACHE: 24 * 60 * 60 * 1000, // 24 hours
  LONG_CACHE: 7 * 24 * 60 * 60 * 1000, // 7 days
  SHORT_CACHE: 60 * 60 * 1000,         // 1 hour

  // Max retry attempts
  MAX_RETRIES: 3,
  // Background refetch interval
  REFETCH_INTERVAL: 5 * 60 * 1000, // 5 minutes
};

// Enhanced QueryClient with intelligent caching
export const createEnhancedQueryClient = () => {
  try {
    const queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          // Global defaults
          staleTime: CACHE_CONFIG.PRODUCT_DATA, // Default 15 minutes
          gcTime: CACHE_CONFIG.DEFAULT_CACHE,   // Garbage collection time
          retry: CACHE_CONFIG.MAX_RETRIES,
          retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

          // Background refetching
          refetchOnWindowFocus: false,
          refetchOnReconnect: true,
          refetchInterval: false, // Set per query basis

          // Network mode for offline capability
          networkMode: 'online',

          // Error handling
          throwOnError: false,
        },
        mutations: {
          retry: 2,
          networkMode: 'online',
        },
      },
    });

    console.log("✅ Enhanced QueryClient created successfully");
    return queryClient;
  } catch (error) {
    console.error("❌ Failed to create QueryClient:", error);
    // Return basic QueryClient as fallback
    return new QueryClient({
      defaultOptions: {
        queries: {
          retry: 1,
          staleTime: 5 * 60 * 1000, // 5 minutes
          throwOnError: false,
        },
        mutations: {
          retry: 1,
        },
      },
    });
  }
};

// Cache key generators for consistent caching
export const cacheKeys = {
  // Product related
  products: ['products'] as const,
  product: (id: string) => ['products', id] as const,
  productsByCategory: (categorySlug: string) => ['products', 'category', categorySlug] as const,
  productsByShop: (shopId: string) => ['products', 'shop', shopId] as const,
  productsSearch: (query: string, filters?: Record<string, any>) => 
    ['products', 'search', query, filters] as const,
  featuredProducts: () => ['products', 'featured'] as const,
  // Category related
  categories: ['categories'] as const,
  category: (slug: string) => ['categories', slug] as const,
  // Shop related
  shops: ['shops'] as const,
  shop: (id: string) => ['shops', id] as const,
  myShop: (userId: string) => ['shops', 'my', userId] as const,
  shopByHandle: (handle: string) => ['shops', 'handle', handle] as const,
  // User related
  user: (id: string) => ['user', id] as const,
  userProfile: (id: string) => ['user', 'profile', id] as const,
  // Orders related
  orders: (userId: string) => ['orders', userId] as const,
  order: (id: string) => ['orders', id] as const,
  // Cart related
  cart: (userId: string) => ['cart', userId] as const,
  cartCount: (userId: string) => ['cart', 'count', userId] as const,
  // Analytics related
  analytics: (shopId: string) => ['analytics', shopId] as const,
  shopAnalytics: (shopId: string, timeRange?: string) => 
    ['analytics', 'shop', shopId, timeRange] as const,
};

// Cache utilities
export const cacheUtils = {
  // Warm critical cache with essential data
  warmCache: async (queryClient: QueryClient) => {
    try {
      console.log('🔥 Starting cache warm-up...');

      // Simple prefetch without complex logic
      setTimeout(async () => {
        try {
          if (!isSupabaseConfigured) {
            console.log('⚠️ Supabase not configured, skipping cache warm-up');
            return;
          }

          // Pre-fetch categories (safe)
          queryClient.prefetchQuery({
            queryKey: cacheKeys.categories,
            queryFn: async () => {
              try {
                const { data } = await supabase.from('categories').select('*').order('name');
                return data ?? [];
              } catch (error) {
                console.warn('Cache warm-up: categories failed', error);
                return [];
              }
            },
            staleTime: CACHE_CONFIG.STATIC_DATA,
          });

          console.log('✅ Cache warm-up completed');
        } catch (error) {
          console.warn('Cache warm-up failed:', error);
        }
      }, 1000);

    } catch (error) {
      console.warn('Cache warm-up initialization failed:', error);
    }
  },

  // Invalidate related cache when data changes
  invalidateRelated: (queryClient: QueryClient, keys: string[][]) => {
    try {
      keys.forEach(key => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    } catch (error) {
      console.warn('Cache invalidation failed:', error);
    }
  },

  // Optimistic updates helper
  optimisticUpdate: <T>(
    queryClient: QueryClient,
    queryKey: readonly unknown[],
    updater: (old: T | undefined) => T
  ) => {
    try {
      // Cancel outgoing refetches
      queryClient.cancelQueries({ queryKey });

      // Snapshot previous value
      const previousValue = queryClient.getQueryData<T>(queryKey);

      // Optimistically update
      queryClient.setQueryData<T>(queryKey, updater);

      // Return rollback function
      return () => {
        queryClient.setQueryData(queryKey, previousValue);
      };
    } catch (error) {
      console.warn('Optimistic update failed:', error);
      return () => {}; // No-op rollback
    }
  },

  // Clear all cache
  clearAll: (queryClient: QueryClient) => {
    try {
      queryClient.clear();
      localStorage.removeItem('BAHAMARKET_CACHE');
      console.log('🗑️ All cache cleared');
    } catch (error) {
      console.warn('Cache clear failed:', error);
    }
  },

  // Clear specific cache patterns
  clearPattern: (queryClient: QueryClient, pattern: string[]) => {
    try {
      queryClient.invalidateQueries({ queryKey: pattern });
      console.log(`🗑️ Cache cleared for pattern:`, pattern);
    } catch (error) {
      console.warn('Cache pattern clear failed:', error);
    }
  },

  // Get cache stats
  getCacheStats: (queryClient: QueryClient) => {
    try {
      const cache = queryClient.getQueryCache();
      const queries = cache.getAll();

      return {
        totalQueries: queries.length,
        freshQueries: queries.filter(q => q.state.dataUpdatedAt > Date.now() - (q.options.staleTime || 0)).length,
        staleQueries: queries.filter(q => q.state.dataUpdatedAt <= Date.now() - (q.options.staleTime || 0)).length,
        errorQueries: queries.filter(q => q.state.status === 'error').length,
        loadingQueries: queries.filter(q => q.state.status === 'pending').length,
        cacheSize: 0, // Simplified
      };
    } catch (error) {
      console.warn('Cache stats failed:', error);
      return {
        totalQueries: 0,
        freshQueries: 0,
        staleQueries: 0,
        errorQueries: 0,
        loadingQueries: 0,
        cacheSize: 0,
      };
    }
  },
};

// Background sync utilities
export const backgroundSync = {
  // Start background sync for critical data
  startSync: (queryClient: QueryClient) => {
    try {
      console.log('🔄 Background sync initialized');

      // Simple interval-based sync (less aggressive)
      setTimeout(() => {
        try {
          queryClient.invalidateQueries({ queryKey: cacheKeys.categories });
        } catch (error) {
          console.warn('Background sync failed:', error);
        }
      }, CACHE_CONFIG.STATIC_DATA);

    } catch (error) {
      console.warn('Background sync start failed:', error);
    }
  },

  // Force refresh critical data
  forceRefresh: (queryClient: QueryClient) => {
    try {
      const criticalKeys = [
        cacheKeys.categories,
        cacheKeys.featuredProducts(),
      ];

      criticalKeys.forEach(key => {
        queryClient.refetchQueries({ queryKey: key });
      });
    } catch (error) {
      console.warn('Force refresh failed:', error);
    }
  },
};

// Performance monitoring
export const cachePerformance = {
  // Monitor cache hit rate
  trackCacheHit: (key: string) => {
    try {
      const hits = parseInt(localStorage.getItem('cache_hits') || '0') + 1;
      localStorage.setItem('cache_hits', hits.toString());
      if (import.meta.env.DEV) {
        console.log(`📊 Cache hit for ${key}. Total hits: ${hits}`);
      }
    } catch (error) {
      // Silent fail
    }
  },

  // Monitor cache miss rate
  trackCacheMiss: (key: string) => {
    try {
      const misses = parseInt(localStorage.getItem('cache_misses') || '0') + 1;
      localStorage.setItem('cache_misses', misses.toString());
      if (import.meta.env.DEV) {
        console.log(`📊 Cache miss for ${key}. Total misses: ${misses}`);
      }
    } catch (error) {
      // Silent fail
    }
  },

  // Get cache performance stats
  getStats: () => {
    try {
      const hits = parseInt(localStorage.getItem('cache_hits') || '0');
      const misses = parseInt(localStorage.getItem('cache_misses') || '0');
      const total = hits + misses;

      return {
        hits,
        misses,
        total,
        hitRate: total > 0 ? (hits / total * 100).toFixed(2) + '%' : '0%',
      };
    } catch (error) {
      return {
        hits: 0,
        misses: 0,
        total: 0,
        hitRate: '0%',
      };
    }
  },

  // Reset performance tracking
  reset: () => {
    try {
      localStorage.removeItem('cache_hits');
      localStorage.removeItem('cache_misses');
    } catch (error) {
      // Silent fail
    }
  },
};